import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getBrowserCountryCode } from '../../api/Api';
import {loadStartupAnnouncement} from '../../api/storefrontStartup';
import './OfferBand.css';

export default function OfferBand({ offers }) {
    const [index, setIndex] = useState(0);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [paused, setPaused] = useState(false);
    const [remoteOffers, setRemoteOffers] = useState(null);
    const countryRef = useRef(null);
    const items = Array.isArray(offers)
        ? offers.filter((offer) => offer && typeof offer.message === 'string' && offer.message.trim())
        : Array.isArray(remoteOffers)
            ? remoteOffers.filter((offer) => offer && typeof offer.message === 'string' && offer.message.trim())
            : [];
    const count = items.length;
    const loading = !Array.isArray(offers) && remoteOffers === null;

    useEffect(() => {
        if (Array.isArray(offers)) return undefined;
        let mounted = true;
        let requestVersion = 0;
        const loadAnnouncement = async () => {
            const version = ++requestVersion;
            const currentCountry = localStorage.getItem('country_id') || getBrowserCountryCode();
            countryRef.current = currentCountry;
            setRemoteOffers(null);
            try {
                const response = await loadStartupAnnouncement(currentCountry);
                if (!mounted || version !== requestVersion) return;
                const payload = response?.data;
                const announcements = Array.isArray(payload?.offers) ? payload.offers
                    : Array.isArray(payload?.data) ? payload.data : payload?.data ? [payload.data] : [];
                setRemoteOffers(announcements.filter(announcement => announcement && typeof announcement.message === 'string' && announcement.message.trim()).map(announcement => ({
                    id: announcement.id || 'announcement-bar',
                    message: announcement.message,
                    href: announcement.action?.url || '',
                    actionLabel: announcement.action?.label || '',
                })));
                countryRef.current = localStorage.getItem('country_id') || currentCountry;
                setIndex(0);
            } catch (error) {
                if (mounted && version === requestVersion) {
                    setRemoteOffers([]);
                    console.error('Unable to load announcement bar', error);
                }
            }
        };
        void loadAnnouncement();

        const handleCountryChange = () => {
            const nextCountry = localStorage.getItem('country_id') || getBrowserCountryCode();
            if (nextCountry !== countryRef.current) void loadAnnouncement();
        };
        const handleStorageChange = (event) => {
            if (event.key === 'country_id') handleCountryChange();
        };
        window.addEventListener('country-changed', handleCountryChange);
        window.addEventListener('storage', handleStorageChange);
        return () => {
            mounted = false;
            window.removeEventListener('country-changed', handleCountryChange);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [offers]);

    useEffect(() => {
        const motion = typeof window.matchMedia === 'function'
            ? window.matchMedia('(prefers-reduced-motion: reduce)')
            : {matches: false};
        if (count < 2 || hovered || focused || paused || motion.matches) return undefined;
        const timer = window.setInterval(() => setIndex((current) => (current + 1) % count), 6000);
        return () => window.clearInterval(timer);
    }, [count, hovered, focused, paused]);

    if (loading) return <section className="ps-offer-band ps-offer-band--loading" role="status" aria-label="Loading promotions" aria-busy="true">
        <div className="container ps-offer-band__inner" aria-hidden="true">
            <div className="ps-offer-band__signature"><span className="ps-offer-skeleton ps-offer-skeleton--seal"/><span className="ps-offer-skeleton ps-offer-skeleton--heritage"/></div>
            <div className="ps-offer-band__message"><span className="ps-offer-skeleton ps-offer-skeleton--icon"/><span className="ps-offer-skeleton ps-offer-skeleton--message"/><span className="ps-offer-skeleton ps-offer-skeleton--button"/></div>
            <div className="ps-offer-band__controls"><span className="ps-offer-skeleton ps-offer-skeleton--controls"/></div>
        </div>
    </section>;
    if (!count) return null;
    const activeIndex = index % count;
    const offer = items[activeIndex];

    return (
        <section className={`ps-offer-band${paused ? ' ps-offer-band--paused' : ''}`} aria-label="Store promotions"
            onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
            onFocus={() => setFocused(true)}
            onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
            <div className="container ps-offer-band__inner">
                <div className="ps-offer-band__signature">
                    <span className="ps-offer-band__seal" aria-hidden="true"><i className="fa fa-sun-o" /></span>
                    <span className="ps-offer-band__heritage"><span>Rooted in</span><strong>tradition</strong></span>
                </div>
                <div className="ps-offer-band__message">
                    <span className="ps-offer-band__icon"><i className="fa fa-tag" aria-hidden="true" /></span>
                    <div className="ps-offer-band__copy">
                        <strong key={offer.id || activeIndex}>{offer.message}</strong>
                    </div>
                    {offer.href && <Link className="ps-offer-band__link" to={offer.href}>{offer.actionLabel || 'Shop now'}<span aria-hidden="true">↗</span></Link>}
                </div>
                {count > 1 && <div className="ps-offer-band__controls">
                    <div className="ps-offer-band__dots" role="group" aria-label="Choose promotion">
                        {items.map((item, position) => <button type="button" key={item.id || position}
                            className={position === activeIndex ? 'is-active' : ''}
                            aria-label={`Show promotion ${position + 1}: ${item.message}`} aria-pressed={position === activeIndex}
                            onClick={() => setIndex(position)} />)}
                    </div>
                    <button type="button" className="ps-offer-band__pause" aria-label={paused ? 'Play promotions' : 'Pause promotions'}
                        onClick={() => setPaused((current) => !current)}><i className={`fa ${paused ? 'fa-play' : 'fa-pause'}`} aria-hidden="true" /></button>
                </div>}
            </div>
        </section>
    );
}
